import { CustomButton } from "@/components/custom/custom-button";
import { CustomErrorMessage } from "@/components/custom/custom-error-message";
import { FormTextInput } from "@/components/custom/custom-form-text-input";
import { FormSelectInput } from "@/components/custom/custom-form-select-input";
import { showToast } from "@/redux/actions/toast-actions";
import { updateProductFeeSchema } from "@/schemas/pricing-config.schema";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  InputAdornment,
  Typography,
  styled,
  Stack,
} from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useFormik } from "formik";
import { useDispatch } from "react-redux";
import { useProductCategories } from "@/hooks/use-products-categories";
import { useAllProducts } from "@/hooks/use-all-products";

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    borderRadius: "12px",
    padding: theme.spacing(2),
    maxWidth: 500,
    width: "100%",
  },
}));

const transformPayload = (values) => ({
  categoryId: Number(values.categoryId),
  productId: Number(values.productId),
  percentage: Number(values.percentage),
  capAmount: Number(values.capAmount),
});

const ProductCategoryFeeDialog = ({
  open,
  onClose,
  mutationFn,
  categoryFee,
}) => {
  const isEditing = Boolean(categoryFee);
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  // Gated on `open` so an unopened dialog doesn't fetch categories and
  // products on every page load.
  const { data: categoriesData } = useProductCategories(open);
  const { data: productsData } = useAllProducts(open, { page: 0, size: 100 });

  const categoryOptions = (categoriesData?.data?.data ?? []).map((cat) => ({
    label: cat.name,
    value: cat.id,
  }));

  const productOptions = (productsData?.data?.data ?? []).map((prod) => ({
    label: prod.name,
    value: prod.id,
  }));

  const formik = useFormik({
    initialValues: {
      categoryId: categoryFee?.category?.id ?? "",
      productId: categoryFee?.product?.id ?? "",
      percentage: categoryFee?.percentage ?? "",
      capAmount: categoryFee?.capAmount ?? "",
    },
    enableReinitialize: true,
    validationSchema: updateProductFeeSchema,
    validateOnMount: true,
    onSubmit: (values) => {
      mutation.mutate(transformPayload(values));
    },
  });

  const mutation = useMutation({
    mutationFn: (payload) => mutationFn(payload),
    onSuccess: (data) => {
      formik.resetForm();
      dispatch(
        showToast(
          data?.message ?? (isEditing ? "Fee updated" : "Fee created"),
          "success",
        ),
      );
      queryClient.invalidateQueries({ queryKey: ["product-category-fees"] });
      onClose();
    },
    onError: (err) => {
      dispatch(showToast(err?.message ?? "Something went wrong", "error"));
    },
  });

  const handleClose = () => {
    if (mutation.isPending) return;
    formik.resetForm();
    mutation.reset();
    onClose();
  };

  return (
    <StyledDialog open={open} onClose={handleClose}>
      <DialogContent
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          px: 2,
          pt: 2,
          pb: 0,
        }}
      >
        <Box>
          <DialogTitle
            sx={{
              p: 0,
              fontSize: "1.1rem",
              fontWeight: 600,
              color: "text.primary",
            }}
          >
            {isEditing
              ? "Update Product Category Fee"
              : "Create Product Category Fee"}
          </DialogTitle>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            {isEditing
              ? "Edit the details below to update this fee configuration."
              : "Fill in the details below to create a new fee configuration."}
          </Typography>
        </Box>

        <Stack spacing={2}>
          <FormSelectInput
            options={categoryOptions}
            formik={formik}
            name="categoryId"
            label="Category"
          />

          <FormSelectInput
            options={productOptions}
            formik={formik}
            name="productId"
            label="Product"
          />

          <FormTextInput
            formik={formik}
            name="percentage"
            label="Percentage"
            type="number"
            InputProps={{
              endAdornment: <InputAdornment position="end">%</InputAdornment>,
            }}
          />

          <FormTextInput
            formik={formik}
            name="capAmount"
            label="Local fee cap"
            type="number"
            placeholder="0.00"
            InputProps={{
              startAdornment: <InputAdornment position="start">NGN</InputAdornment>,
            }}
          />
        </Stack>

        {mutation.isError && (
          <CustomErrorMessage
            message={
              (Array.isArray(mutation.error?.data)
                ? mutation.error.data[0]
                : null) ??
              mutation.error?.message ??
              "Failed. Try again."
            }
          />
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2, pt: 2, pb: 1, gap: 1 }}>
        <CustomButton
          variantType="outline"
          size="small"
          onClick={handleClose}
          disabled={mutation.isPending}
          sx={{ px: 2 }}
        >
          Cancel
        </CustomButton>
        <CustomButton
          size="small"
          disabled={!formik.isValid || !formik.dirty || mutation.isPending}
          isLoading={mutation.isPending}
          onClick={formik.handleSubmit}
          sx={{ px: 2 }}
        >
          {mutation.isPending
            ? isEditing
              ? "Updating..."
              : "Creating..."
            : isEditing
              ? "Update Fee"
              : "Create Fee"}
        </CustomButton>
      </DialogActions>
    </StyledDialog>
  );
};

export default ProductCategoryFeeDialog;
